import React, { useState, useEffect } from "react";
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  CheckCircle,
  AlertCircle,
  Loader2,
  Save,
  ArrowLeft,
  Shield
} from 'lucide-react';
import { useNavigate } from "react-router-dom";

const API_BASE_URL = 'https://aaapd.org.et/gallery_backend/api';

export default function AssociationEmailUpdate() {
  const [currentEmail, setCurrentEmail] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', null
  const [showPassword, setShowPassword] = useState(false);
  const [adminId, setAdminId] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    async function loadCurrentEmail() {
      setLoading(true);
      try {
        const currentUser = JSON.parse(localStorage.getItem("currentUser"));
        const email = currentUser?.email;

        if (!email) {
          setSubmitStatus('error');
          setErrors({ submit: 'No logged-in user found.' });
          return;
        }

        setCurrentEmail(email);

        // Fetch Admin by email to get admin_id
        const response = await fetch(`${API_BASE_URL}/get_admin_by_email.php?email=${encodeURIComponent(email)}`);
        const data = await response.json();

        if (data.status === 'success' && data.data) {
          setAdminId(data.data.id);
        }
      } catch (err) {
        console.error("Error loading OPD email:", err);
        setSubmitStatus('error');
        setErrors({ submit: 'Failed to load email info' });
      } finally {
        setLoading(false);
      }
    }
    loadCurrentEmail();
  }, []);

  const handleChange = (field, value) => {
    if (field === 'newEmail') setNewEmail(value);
    if (field === 'confirmEmail') setConfirmEmail(value);
    if (field === 'password') setPassword(value);
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    setSubmitStatus(null);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!password.trim()) {
      newErrors.password = 'Please enter your current password to confirm.';
    }
    
    if (!newEmail.trim()) {
      newErrors.newEmail = 'Please enter a new email address.';
    } else if (!/\S+@\S+\.\S+/.test(newEmail)) {
      newErrors.newEmail = 'Please enter a valid email address.';
    } else if (newEmail === currentEmail) {
      newErrors.newEmail = 'New email must be different from current email.';
    }
    
    if (!confirmEmail.trim()) {
      newErrors.confirmEmail = 'Please confirm your new email address.';
    } else if (newEmail !== confirmEmail) {
      newErrors.confirmEmail = 'Email addresses do not match.';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setSubmitStatus('error');
      return;
    }

    setSubmitting(true);
    setSubmitStatus(null);

    try {
      // Send email update request to backend
      const response = await fetch(`${API_BASE_URL}/update_admin_email.php`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          current_email: currentEmail,
          new_email: newEmail,
          password: password,
          admin_id: adminId,
        }),
      });

      const data = await response.json();

      if (data.status === 'success') {
        setSubmitStatus('success');

        // Update localStorage with new email
        localStorage.setItem("currentUser", JSON.stringify({ email: newEmail }));

        setCurrentEmail(newEmail);
        setNewEmail("");
        setConfirmEmail("");
        setPassword("");
        setErrors({});

        // Redirect after success
        setTimeout(() => {
          navigate('/adminsettings');
        }, 2000);
      } else {
        setSubmitStatus('error');
        setErrors({ submit: data.message || 'Failed to update email. Please try again.' });
      }
    } catch (err) {
      console.error("Error updating email:", err);
      setSubmitStatus('error');
      setErrors({ submit: 'Failed to update email. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-gray-100 min-h-screen p-4 md:p-8">
        <div className="text-center py-16">
          <Mail className="w-16 h-16 text-gray-400 mx-auto mb-4 animate-pulse" />
          <p className="text-gray-500 text-lg">Loading email settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 min-h-screen p-4 md:p-8">
      {/* Header */}
      <header className="mb-8">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/adminsettings')}
            className="p-2 hover:bg-white rounded-lg transition-colors"
          >
            <ArrowLeft className="w-6 h-6 text-gray-600" />
          </button>
          <div className="flex items-center gap-3">
            <Mail className="w-8 h-8 text-teal-600" />
            <div>
              <h1 className="text-4xl font-bold text-gray-800">Update Email</h1>
              <p className="text-gray-500">Change your account email address</p>
            </div>
          </div>
        </div>
      </header>

      {/* Form */}
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white p-6">
            <div className="flex items-center gap-3">
              <Shield className="w-6 h-6" />
              <div>
                <h2 className="text-2xl font-bold">Email Security Update</h2>
                <p className="text-teal-100 mt-1">Verify your identity to change your email address</p>
              </div>
            </div>
          </div>

          <div className="p-6">
            {/* Status Messages */}
            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <div>
                  <p className="text-green-800 font-semibold">Email Updated Successfully!</p>
                  <p className="text-green-700 text-sm">Redirecting you back to your profile...</p>
                </div>
              </div>
            )}

            {submitStatus === 'error' && errors.submit && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <div>
                  <p className="text-red-800 font-semibold">Update Failed</p>
                  <p className="text-red-700 text-sm">{errors.submit}</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Current Email Section */}
              <div>
                <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200">
                  <Mail className="w-5 h-5 text-teal-600" />
                  <h3 className="text-lg font-semibold text-gray-800">Current Email</h3>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <div className="flex items-center gap-1">
                      <Mail className="w-4 h-4" />
                      Current Email Address
                    </div>
                  </label>
                  <input
                    type="email"
                    value={currentEmail}
                    disabled
                    className="w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 bg-gray-50 text-gray-700 cursor-not-allowed"
                  />
                </div>
              </div>

              {/* New Email Section */}
              <div>
                <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200">
                  <Mail className="w-5 h-5 text-teal-600" />
                  <h3 className="text-lg font-semibold text-gray-800">New Email Address</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <div className="flex items-center gap-1">
                        <Mail className="w-4 h-4" />
                        New Email *
                      </div>
                    </label>
                    <input
                      type="email"
                      value={newEmail}
                      onChange={(e) => handleChange('newEmail', e.target.value)}
                      className={`w-full border rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-teal-500 transition ${
                        errors.newEmail ? 'border-red-300 bg-red-50' : 'border-gray-300'
                      }`}
                      placeholder="Enter new email address"
                    />
                    {errors.newEmail && <p className="text-red-600 text-sm mt-1">{errors.newEmail}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <div className="flex items-center gap-1">
                        <Mail className="w-4 h-4" />
                        Confirm New Email *
                      </div>
                    </label>
                    <input
                      type="email"
                      value={confirmEmail}
                      onChange={(e) => handleChange('confirmEmail', e.target.value)}
                      className={`w-full border rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-teal-500 transition ${
                        errors.confirmEmail ? 'border-red-300 bg-red-50' : 'border-gray-300'
                      }`}
                      placeholder="Confirm new email address"
                    />
                    {errors.confirmEmail && <p className="text-red-600 text-sm mt-1">{errors.confirmEmail}</p>}
                  </div>
                </div>
              </div>

              {/* Password Verification Section */}
              <div>
                <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200">
                  <Lock className="w-5 h-5 text-teal-600" />
                  <h3 className="text-lg font-semibold text-gray-800">Security Verification</h3>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <div className="flex items-center gap-1">
                      <Lock className="w-4 h-4" />
                      Current Password *
                    </div>
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => handleChange('password', e.target.value)}
                      className={`w-full border rounded-lg shadow-sm py-2 px-3 pr-10 focus:outline-none focus:ring-2 focus:ring-teal-500 transition ${
                        errors.password ? 'border-red-300 bg-red-50' : 'border-gray-300'
                      }`}
                      placeholder="Enter your current password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {errors.password && <p className="text-red-600 text-sm mt-1">{errors.password}</p>}
                  <p className="text-gray-500 text-sm mt-1">Enter your current password to confirm this change</p>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => navigate('/adminsettings')}
                  className="px-6 py-2 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex items-center gap-2 bg-gradient-to-r from-teal-600 to-teal-700 text-white font-semibold py-2 px-6 rounded-lg hover:from-teal-700 hover:to-teal-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Updating Email...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Update Email
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
